// =============================================================================
// TIME SELECTOR COMPONENT
// =============================================================================
// Reusable time slot selector component for appointment scheduling

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/Button';
import { availabilityService, type TimeSlot } from '@/services/availabilityService';
import { useToastNotifications } from '@/hooks/useToastNotifications';
import '../../styles/_timeselector.scss';

interface TimeSelectorProps {
  selectedDate: Date;
  selectedTimeSlot: TimeSlot | null;
  onDateChange: (date: Date) => void;
  onTimeSlotSelect: (slot: TimeSlot) => void;
  className?: string;
  disabled?: boolean;
}

export const TimeSelector: React.FC<TimeSelectorProps> = ({
  selectedDate,
  selectedTimeSlot,
  onDateChange,
  onTimeSlotSelect,
  className = '',
  disabled = false
}) => {
  const toast = useToastNotifications();
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Load available slots when date changes
  useEffect(() => {
    if (!disabled) {
      loadAvailableSlots();
    }
  }, [selectedDate, disabled]);

  const loadAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(selectedDate);
      endDate.setHours(23, 59, 59, 999);

      const response = await availabilityService.getAvailableSlots(startDate, endDate);
      
      if (response.success && response.timeSlots) {
        setAvailableSlots(response.timeSlots);
      } else {
        toast.showError(response.error || 'Failed to load available time slots');
        setAvailableSlots([]);
      }
    } catch (error) {
      // Error loading available slots
      toast.showError('Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateChange = (date: Date) => {
    onDateChange(date);
  };

  const handleTimeSlotSelect = (slot: TimeSlot) => {
    if (slot.available && !disabled) {
      onTimeSlotSelect(slot);
    }
  };

  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAvailableSlotsForDate = (): TimeSlot[] => {
    return availableSlots.filter(slot => slot.available);
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    handleDateChange(newDate);
  };

  if (disabled) {
    return (
      <div className={`time-selector disabled ${className}`}>
        <div className="time-selector-header">
          <h4>Appointment Time</h4>
          <p className="disabled-message">Select a payment method first to choose your appointment time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`time-selector ${className}`}>
      <div className="time-selector-header">
        <h4>Select Appointment Time</h4>
        <p>Choose a convenient time slot for your session.</p>
      </div>

      {/* Date Navigation */}
      <div className="date-navigation">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigateDate('prev')}
          disabled={loadingSlots}
        >
          ← Previous
        </Button>
        
        <div className="selected-date">
          {formatDate(selectedDate)}
        </div>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigateDate('next')}
          disabled={loadingSlots}
        >
          Next →
        </Button>
      </div>

      {/* Time Slots */}
      <div className="time-slots-container">
        {loadingSlots ? (
          <div className="loading-slots">
            <div className="loading-spinner"></div>
            <p>Loading available time slots...</p>
          </div>
        ) : (
          <div className="time-slots-grid">
            {getAvailableSlotsForDate().length === 0 ? (
              <div className="no-slots">
                <p>No available time slots for this date.</p>
                <p>Please try a different date.</p>
              </div>
            ) : (
              getAvailableSlotsForDate().map((slot, index) => (
                <button
                  key={index}
                  type="button"
                  className={`time-slot ${selectedTimeSlot?.start === slot.start ? 'selected' : ''}`}
                  onClick={() => handleTimeSlotSelect(slot)}
                  disabled={disabled}
                >
                  {formatTime(slot.start)}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {selectedTimeSlot && (
        <div className="selected-slot-info">
          <p>
            <strong>Selected:</strong> {formatDate(selectedDate)} at {formatTime(selectedTimeSlot.start)}
          </p>
        </div>
      )}
    </div>
  );
};

export default TimeSelector;
